import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { LockKey, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

export default function PublicGatePage() {
  const { pid } = useParams();
  const [params] = useSearchParams();
  const cid = params.get("cid");
  const [pub, setPub] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.publicPublication(pid, cid)
      .then(d => {
        setPub(d);
        if (d.prefill) setForm({ name: d.prefill.name || "", company: d.prefill.company || "", email: d.prefill.email || "" });
      })
      .catch(() => setNotFound(true));
  }, [pid, cid]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) { setError("Please fill in your name and email address."); return; }
    setBusy(true);
    try {
      await api.submitPubRequest(pid, { ...form, cid: cid || null });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  const brand = pub?.brand?.brand_color || "#005191";

  if (notFound) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-200 rounded-xl p-10 max-w-md text-center" data-testid="gate-not-found">
        <h1 className="font-heading text-lg font-medium text-zinc-900">Publication not available</h1>
        <p className="text-sm text-zinc-500 mt-2">This publication doesn't exist or is no longer available on request.</p>
      </div>
    </div>
  );

  if (!pub) return <div className="min-h-screen bg-zinc-50 flex items-center justify-center text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm" data-testid="gate-card">
          <div style={{ backgroundColor: brand }} className="px-8 py-6">
            {pub.brand?.logo_url ? (
              <img src={pub.brand.logo_url} alt={pub.brand.from_name} className="h-9 object-contain" style={{ filter: "brightness(0) invert(1)" }}/>
            ) : (
              <div className="text-white font-heading font-semibold text-lg">{pub.brand?.from_name}</div>
            )}
          </div>
          <div className="p-8">
            {!done ? (
              <>
                <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-zinc-400 mb-2">
                  <LockKey size={13}/> Gated publication
                </div>
                <h1 className="font-heading text-xl font-medium text-zinc-900" data-testid="gate-title">{pub.title}</h1>
                {pub.description && <p className="text-sm text-zinc-500 mt-2 whitespace-pre-line" data-testid="gate-description">{pub.description}</p>}
                <p className="text-sm text-zinc-600 mt-4">Leave your details below and we'll email you a copy once your request is approved.</p>
                <form onSubmit={submit} className="mt-5 space-y-3">
                  <div>
                    <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Full name</label>
                    <input data-testid="gate-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" className="h-10 w-full rounded-md border border-zinc-300 px-3 text-sm focus:outline-none focus:ring-2" style={{ "--tw-ring-color": brand }}/>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Company</label>
                    <input data-testid="gate-company-input" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Acme Pharma" className="h-10 w-full rounded-md border border-zinc-300 px-3 text-sm focus:outline-none focus:ring-2"/>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-1">Work email</label>
                    <input data-testid="gate-email-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jane@acme.com" className="h-10 w-full rounded-md border border-zinc-300 px-3 text-sm focus:outline-none focus:ring-2"/>
                  </div>
                  {error && <p className="text-xs text-rose-600" data-testid="gate-error">{error}</p>}
                  <button data-testid="gate-submit-btn" type="submit" disabled={busy} style={{ backgroundColor: brand }}
                    className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-md text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity">
                    <PaperPlaneTilt size={15}/> {busy ? "Sending…" : "Request my copy"}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-6" data-testid="gate-success">
                <CheckCircle size={44} weight="fill" style={{ color: brand }} className="mx-auto"/>
                <h2 className="font-heading text-lg font-medium text-zinc-900 mt-3">Request received</h2>
                <p className="text-sm text-zinc-500 mt-2">Thanks {form.name.split(" ")[0]}, your request for “{pub.title}” has been sent for review. If approved, you'll receive a copy at <strong>{form.email}</strong>.</p>
              </div>
            )}
          </div>
        </div>
        <p className="text-center text-[11px] text-zinc-400 mt-4">{pub.brand?.from_name}</p>
      </div>
    </div>
  );
}
